/**
 * Canvas-rendered leaderboard display for the completion screen.
 *
 * Shows the top 10 game records below the completion timer,
 * with rank, penalized time, raw time, and undo count for each entry.
 *
 * Layout on the completion screen:
 *   Row 0: Timer + New Game button (drawn by drawTimer.ts / drawButtons.ts)
 *   Row 1: Current game summary (time, undos, penalty)
 *   Row 2: "BEST TIMES" header
 *   Rows 2.5–8: Up to 10 records as compact lines
 */

import { appState } from './state'
import { getUndoCount } from './state'
import { timerGetElapsed, formatTime } from './timer'
import { loadRecords } from './recordsManager'
import { UNDO_PENALTY_MS } from '../Models/GameRecord'
import { rowY } from './allXYCoordinates'
import { CARD_FONT } from './canvasMethods'

let _debugDrawRecordsCallCount = 0

export function getDrawRecordsDebugCounter(): number {
    return _debugDrawRecordsCallCount
}

export function resetDrawRecordsDebugCounter(): void {
    _debugDrawRecordsCallCount = 0
}

/**
 * Draw a semi-transparent backdrop panel behind the completion screen content.
 * Called from paint.ts right after drawWinStaticFrame, before the timer,
 * new game button, and records so all text is readable over trail stamps.
 */
export function drawCompletionBackdrop(ctx: CanvasRenderingContext2D): void {
    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const winH = appState.windowHeight

    // Panel spans from above row 0 to the bottom of the screen
    const panelPadding = Math.floor(ch * 0.3)
    const panelX = panelPadding
    const panelY = rowY(0) - Math.floor(ch * 0.15)
    const panelW = winW - panelPadding * 2
    const panelH = winH - panelY - panelPadding
    const panelR = Math.max(8, Math.floor(ch * 0.15))

    ctx.save()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.82)'
    ctx.beginPath()
    ctx.roundRect(panelX, panelY, panelW, panelH, panelR)
    ctx.fill()
    ctx.restore()
}

/**
 * Draw the current game summary and leaderboard on the completion screen.
 * Called from paint.ts after the win animation finishes.
 */
export function drawRecords(ctx: CanvasRenderingContext2D): void {
    _debugDrawRecordsCallCount++

    // Keep backward compatibility with e2e checks that assert this function
    // runs immediately after wins (Issue #19).
    // Actual visibility is controlled by appState.showWinRecords.
    if (!appState.showWinRecords) {
        return
    }

    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const highlightColor = appState.currentTheme.highlightColor

    // ── Color palette (designed for the dark completion backdrop) ──────
    const primaryColor = 'rgba(255, 255, 255, 0.9)'
    const secondaryColor = 'rgba(255, 255, 255, 0.6)'
    const mutedColor = 'rgba(255, 255, 255, 0.35)'

    const centerX = Math.floor(winW / 2)

    // ── Row 1: Current game summary ─────────────────────────────────────
    const summaryY = rowY(1) + Math.floor(ch / 2)
    const undoCount = getUndoCount()
    const rawTime = timerGetElapsed()
    const summaryFontSize = Math.floor(ch * 0.2)

    ctx.save()
    ctx.font = `500 ${summaryFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = secondaryColor

    if (undoCount > 0) {
        const penalty = undoCount * UNDO_PENALTY_MS
        const penalizedTime = rawTime + penalty
        const line1 = `${formatTime(rawTime)} + ${undoCount} undo${undoCount !== 1 ? 's' : ''} (+${formatTime(penalty)})`
        const line2 = `Final: ${formatTime(penalizedTime)}`

        const lineSpacing = Math.floor(summaryFontSize * 1.7)
        ctx.fillText(line1, centerX, summaryY - Math.floor(lineSpacing * 0.45))
        ctx.font = `600 ${summaryFontSize}px ${CARD_FONT}`
        ctx.fillStyle = primaryColor
        ctx.fillText(line2, centerX, summaryY + Math.floor(lineSpacing * 0.55))
    } else {
        ctx.fillText('No undos used', centerX, summaryY)
    }
    ctx.restore()

    // ── Load records ────────────────────────────────────────────────────
    const records = loadRecords()
    if (records.length === 0) return

    // ── Row 2: "BEST TIMES" header ──────────────────────────────────────
    const headerY = rowY(2) + Math.floor(ch * 0.35)
    const headerFontSize = Math.floor(ch * 0.18)

    ctx.save()
    ctx.font = `700 ${headerFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = mutedColor
    ctx.letterSpacing = '2px'
    ctx.fillText('BEST TIMES', centerX, headerY)
    ctx.restore()

    // ── Records list ────────────────────────────────────────────────────
    // Start below the header, fit up to 10 records between rows 2.5 and 8
    const listStartY = rowY(2) + Math.floor(ch * 0.65)
    const listEndY = rowY(8) + Math.floor(ch * 0.5)
    const availableHeight = listEndY - listStartY

    const recordFontSize = Math.floor(ch * 0.17)
    const lineHeight = Math.min(
        Math.floor(recordFontSize * 2.2),
        Math.floor(availableHeight / Math.min(records.length, 10))
    )

    // Column layout: rank | time | undos
    // All centered around the middle of the screen
    const colWidth = Math.floor(winW * 0.8)
    const colStartX = Math.floor((winW - colWidth) / 2)
    const rankX = colStartX + Math.floor(colWidth * 0.08)
    const timeX = colStartX + Math.floor(colWidth * 0.35)
    const undoX = colStartX + Math.floor(colWidth * 0.72)

    for (let i = 0; i < records.length; i++) {
        const record = records[i]
        const y = listStartY + i * lineHeight

        // Don't render if we've overflowed
        if (y > listEndY) break

        const isTopThree = i < 3

        ctx.save()

        // Rank number
        ctx.font = `${isTopThree ? '700' : '500'} ${recordFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = isTopThree ? highlightColor : mutedColor
        ctx.fillText(`${i + 1}.`, rankX, y)

        // Penalized time (main value)
        ctx.font = `${isTopThree ? '600' : '500'} ${recordFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = isTopThree ? primaryColor : secondaryColor
        ctx.fillText(formatTime(record.penalizedTime), timeX, y)

        // Undo count (if any)
        ctx.font = `400 ${Math.floor(recordFontSize * 0.85)}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (record.undoCount > 0) {
            ctx.fillStyle = mutedColor
            ctx.fillText(`${record.undoCount} undo${record.undoCount !== 1 ? 's' : ''}`, undoX, y)
        } else {
            ctx.fillStyle = mutedColor
            ctx.fillText('clean', undoX, y)
        }

        ctx.restore()
    }
}
